import { isForcingLogout, useAuth } from '~/composables/useAuth';
import { useAuthRuntime } from '~/composables/useAuthRuntime';
import { resolveApiErrorMessage } from '~/constants/apiErrorCodes';
import { useAuthStore } from '~/stores/auth';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorResponse, ApiErrorWithStatus } from '~/types/api';
import { createApiUrl } from '~/utils/api';

// 取得目前語系的函數
// 直接從 document.cookie 讀取 i18n_redirected cookie（由 Nuxt i18n 自動管理）
// 這樣可以確保在普通函數中也能正常運作，不需要 setup context
function getCurrentLocale(): string {
  try {
    if (import.meta.client && typeof document !== 'undefined') {
      try {
        // 從 document.cookie 讀取 i18n_redirected cookie
        const cookies = document.cookie.split(';');
        const i18nCookie = cookies.find((cookie) => cookie.trim().startsWith('i18n_redirected='));

        if (i18nCookie) {
          const locale = i18nCookie.split('=')[1]?.trim();
          // 驗證語系值是否有效
          if (locale && (locale === 'en' || locale === 'zh-TW')) {
            return locale;
          }
        }
      } catch {
        // 如果讀取 cookie 失敗，fallback 到預設語系
      }
    }

    // 使用預設語系（根據 nuxt.config.ts 的 defaultLocale）
    return 'en';
  } catch (error) {
    // 如果發生錯誤，使用預設語系
    return 'en';
  }
}

export interface ApiFetchOptions extends RequestInit {
  handleError?: boolean;
  requireAuth?: boolean;
  customHeaders?: Record<string, string>;
  // 內部標記，用於防止重試循環
  _isRetry?: boolean;
  // 內部標記，表示已經嘗試過刷新 token
  _refreshAttempted?: boolean;
  // 內部標記，表示已經嘗試過刷新 CSRF token
  _csrfAttempted?: boolean;
}

export async function useApiFetch(path: string, options: ApiFetchOptions) {
  const url = createApiUrl(path);
  const headers = new Headers(options.headers);
  const requestMethod = (options.method || 'GET').toUpperCase();

  // 預設以 JSON 送出（避免 fallback 成 text/plain）
  const isFormData = options.body instanceof FormData;
  if (!headers.has('Content-Type') && options.body && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  // 根據目前語系設定 Accept-Language header
  const currentLocale = getCurrentLocale();
  headers.set('Accept-Language', currentLocale);

  // 設定時區 header
  if (import.meta.client && typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      headers.set('X-Timezone', timeZone);
    } catch (error) {
      // 如果無法取得時區，不設定 header（避免影響請求）
      console.warn('無法取得時區資訊:', error);
    }
  }

  // 處理認證
  // 使用 authStore.getToken() 而不是直接讀取 cookie，確保邏輯一致性
  // authStore 會根據當前環境正確讀取 cookie（localhost 或 .momentx.ai）
  let authStore: ReturnType<typeof useAuthStore> | null = null;
  if (options.requireAuth) {
    authStore = useAuthStore();
    const token = authStore.getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // 處理自定義 headers
  // EX : 'ORGUUID: 41eb17eb-f4db-42a5-9f72-6ed6793d5c79'
  if (options.customHeaders) {
    for (const [key, value] of Object.entries(options.customHeaders)) {
      headers.set(key, value);
    }
  }

  // POST/PUT/DELETE/PATCH 請求需要帶 CSRF token
  if (options.requireAuth && shouldSendCsrfToken(requestMethod) && authStore) {
    const csrfToken = authStore.getCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRF-Token', csrfToken);
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
    // 確保 signal 被正確傳遞
    signal: options.signal,
  });

  if (!response.ok) {
    // 429 的 Retry-After 只存在於 header，錯誤物件不帶就等於丟失（呼叫端做不了倒數）。
    // 跨域時需後端 Access-Control-Expose-Headers 放行，讀不到就是 undefined。
    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
    let error: ApiErrorResponse = {};
    try {
      const body = await response.json();
      // V2 錯誤格式：{ error: { code, message } }
      if (
        body !== null &&
        typeof body === 'object' &&
        'error' in body &&
        (body as { error: unknown }).error !== null &&
        typeof (body as { error: unknown }).error === 'object'
      ) {
        const err = (body as { error: Record<string, unknown> }).error;
        // 保留錯誤的額外欄位到 details，讓呼叫端能取用 code/message 以外的資訊
        // （例如 IDENTITY_BINDING_CONFLICT 帶的 existing_binding_id / scope context）。
        // 後端可能把這些欄位放在 error 物件內、或與 error 同層的 top-level，故兩層都合併進來。
        error = {
          code: typeof err.code === 'string' ? err.code : undefined,
          message: typeof err.message === 'string' ? err.message : undefined,
          details: { ...(body as Record<string, unknown>), ...err },
        };
      } else {
        error = body as ApiErrorResponse;
      }
    } catch {
      error = {};
    }

    // 處理 401 錯誤：嘗試刷新 token 並重試請求
    //
    // 只在瀏覽器端做。伺服器端換到的新 token 送不回瀏覽器（Nuxt 的 useCookie 在 SSR 是
    // 掛在 app:rendered 才寫 Set-Cookie，middleware 以 302 收尾就不會 render），
    // 卻已經把一次性的 refresh token 消費掉 —— 瀏覽器手上那張就此作廢。
    // SSR 遇到 401 一律讓它往上拋，由路由守衛放行、交給 hydrate 之後的瀏覽器端處理。
    if (import.meta.client && response.status === 401 && options.requireAuth && !options._isRetry) {
      // 嘗試刷新 token
      const refreshSuccess = await tryRefreshToken();

      if (refreshSuccess) {
        // 刷新成功，使用新的 token 重試請求
        return useApiFetch(path, {
          ...options,
          _isRetry: true, // 標記為重試，避免無限循環
        });
      }

      // 刷新失敗，處理錯誤並跳轉到登入頁
      // 標記已經嘗試過刷新，避免在 handleApiError 中重複刷新
      await handleApiError(response.status, error, { ...options, _refreshAttempted: true });

      if (options.handleError === false) {
        const errorWithStatus: ApiErrorWithStatus = {
          ...error,
          status: response.status,
          statusCode: response.status,
          retryAfter,
        };
        throw errorWithStatus;
      }
      const apiError = new Error(error.message || 'API request failed') as Error & {
        status?: number;
        statusCode?: number;
        code?: string;
        retryAfter?: number;
      };
      apiError.status = response.status;
      apiError.statusCode = response.status;
      apiError.retryAfter = retryAfter;
      if (error.code) {
        apiError.code = error.code;
      }
      throw apiError;
    }

    // 處理 CSRF 403 錯誤：刷新 token 並重試
    if (
      response.status === 403 &&
      options.requireAuth &&
      shouldSendCsrfToken(requestMethod) &&
      !options._csrfAttempted
    ) {
      const csrfError = getCsrfErrorCode(error);
      if (csrfError === 'csrf_token_missing' || csrfError === 'csrf_token_invalid') {
        const refreshSuccess = await tryRefreshCsrfToken();
        if (refreshSuccess) {
          return useApiFetch(path, {
            ...options,
            _csrfAttempted: true,
          });
        }
      }
    }

    // 統一錯誤處理邏輯
    await handleApiError(response.status, error, options);

    if (options.handleError === false) {
      // 為錯誤物件添加狀態碼，方便 Sentry 過濾
      const errorWithStatus: ApiErrorWithStatus = {
        ...error,
        status: response.status,
        statusCode: response.status,
        retryAfter,
      };
      throw errorWithStatus;
    }
    // 為錯誤物件添加狀態碼，方便 Sentry 過濾
    const apiError = new Error(error.message || 'API request failed') as Error & {
      status?: number;
      statusCode?: number;
      code?: string;
      retryAfter?: number;
    };
    apiError.status = response.status;
    apiError.statusCode = response.status;
    apiError.retryAfter = retryAfter;
    if (error.code) {
      apiError.code = error.code;
    }
    throw apiError;
  }

  return response;
}

/**
 * 嘗試刷新 token（防止並發）
 *
 * 「進行中的那一支」存放在 `useAuthRuntime()` 而不是模組層：伺服器端的模組層是整個
 * Node process 共用的，兩個不同使用者的請求會共用同一支 refresh —— A 換完回 true，
 * B 卻拿著自己那張沒換過的 token 去重試，必然再吃一個 401。
 * 詳見 `composables/useAuthRuntime.ts`。
 */
async function tryRefreshToken(): Promise<boolean> {
  const runtime = useAuthRuntime();

  // 如果已經有正在進行的刷新請求，等待它完成
  if (runtime.apiRefreshTokenPromise) {
    return runtime.apiRefreshTokenPromise;
  }

  // 創建新的刷新請求
  runtime.apiRefreshTokenPromise = (async () => {
    try {
      const auth = useAuth();
      const authStore = useAuthStore();

      // 檢查是否有 refresh token
      if (!authStore.getRefreshToken()) {
        return false;
      }

      // 嘗試刷新 token（不觸發登入流程的重定向）
      const success = await auth.refreshTokenSilently();
      return success;
    } catch (error) {
      console.warn('Token refresh failed:', error);
      return false;
    } finally {
      // 清除 Promise，允許下次刷新
      runtime.apiRefreshTokenPromise = null;
    }
  })();

  return runtime.apiRefreshTokenPromise;
}

// 嘗試刷新 CSRF token（防止並發）
async function tryRefreshCsrfToken(): Promise<boolean> {
  const runtime = useAuthRuntime();

  if (runtime.apiRefreshCsrfPromise) {
    return runtime.apiRefreshCsrfPromise;
  }

  runtime.apiRefreshCsrfPromise = (async () => {
    try {
      const auth = useAuth();
      return await auth.refreshCsrfTokenSilently();
    } catch (error) {
      console.warn('CSRF token refresh failed:', error);
      return false;
    } finally {
      runtime.apiRefreshCsrfPromise = null;
    }
  })();

  return runtime.apiRefreshCsrfPromise;
}
/**
 * 解析 `Retry-After` header → 秒數。RFC 7231 允許兩種格式：
 * delta-seconds（`120`）或 HTTP-date（`Wed, 21 Oct 2026 07:28:00 GMT`），兩種都吃。
 *
 * 讀不到 / 格式不明 / 負數 一律回 undefined，讓呼叫端走自己的 fallback 秒數，
 * 不要回 0（0 會被誤解成「可以立刻重送」，等於沒有限流）。
 */
export function parseRetryAfter(raw: string | null | undefined): number | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  // delta-seconds
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isFinite(seconds) ? seconds : undefined;
  }

  // HTTP-date → 換算成距離現在的秒數
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return undefined;
  const seconds = Math.ceil((at - Date.now()) / 1000);
  return seconds > 0 ? seconds : undefined;
}

function shouldSendCsrfToken(method: string): boolean {
  return method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH';
}

function getCsrfErrorCode(error: ApiErrorResponse): string | undefined {
  if (typeof error.error === 'string') {
    return error.error;
  }
  if (typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
}

// 統一錯誤處理函數
async function handleApiError(status: number, error: ApiErrorResponse, options: ApiFetchOptions) {
  const toast = useToastStore();
  const auth = useAuth();
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  // handleError: false 代表呼叫端要自行處理錯誤顯示（含 toast）
  // 但 401 的登出/跳轉屬於全域副作用，與個別呼叫端無關，仍需執行
  const suppressToast = options.handleError === false;

  // v2.4 業務錯誤碼（附錄 A）：統一文案，避免 402/422/409 等 fall through 成「未知錯誤」。
  // 這些碼與下方依 status 處理的 401/403/404/5xx 不重疊，故先攔截並提前返回。
  // 呼叫端自行處理者（handleError: false）會 suppressToast，不會重複跳 toast。
  // v2.10 額度類錯誤會帶 dimension/used/limit/tier，改用帶數字的文案（「排程已用 15/15」）。
  const resolved = resolveApiErrorMessage(error.code, error.details);
  if (resolved) {
    if (!suppressToast) {
      toast.error(resolved.params ? t(resolved.i18nKey, resolved.params) : t(resolved.i18nKey));
    }
    return;
  }

  switch (status) {
    case 401:
      // 伺服器端對 401 完全不作為。
      //
      // 續期／登出送不回 cookie，反而會用 SSR 的 Set-Cookie 把使用者手上還有效的 token
      // 清掉（見 forceLogoutToLogin）；而 toast store 的內容會被序列化進 SSR payload，
      // 於是「登入已過期」會在 hydration 之後跳出來 —— 即使瀏覽器端接手續期成功了。
      // SSR 的 401 只代表「這一輪拿不到資料」，真偽由瀏覽器端自己再判一次。
      if (import.meta.server) {
        break;
      }

      // 統一處理認證錯誤
      // 已經試過刷新才是真的「登入過期」，此時給明確文案而不是「正在重新登入...」。
      // 並發的多支 401 只有第一支會跳 toast，其餘靠 isForcingLogout() 擋掉。
      if (!suppressToast && !isForcingLogout()) {
        toast.error(
          options._refreshAttempted
            ? t('errorMessages.auth.loginExpired')
            : t('errorMessages.api.unauthorized')
        );
      }

      // 只有在已經嘗試過刷新但失敗的情況下，才執行登出和跳轉
      // 如果 _refreshAttempted 為 true，表示已經在 useApiFetch 中嘗試過刷新但失敗
      // 此時應該直接登出，不應該再調用 refreshTokenOnUnauthorized（避免重複刷新造成循環）
      if (options._refreshAttempted) {
        // 刷新已經試過且失敗 → session 真的結束了。
        // forceLogoutToLogin 內部有併發保護，多支 API 同時 401 也只會登出／跳轉一次。
        await auth.forceLogoutToLogin();
      } else if (options.requireAuth && !options._isRetry) {
        // 如果這是 requireAuth 的請求但沒有嘗試過刷新（理論上不應該發生）
        // 這種情況應該在 useApiFetch 中已經處理，但為了安全起見，這裡也處理
        try {
          await auth.refreshTokenOnUnauthorized();
        } catch (refreshError) {
          console.warn('Token refresh failed, redirecting to login');
        }
      }
      break;
    case 403:
      if (!suppressToast) toast.error(t('errorMessages.api.forbidden'));
      break;
    case 404:
      if (!suppressToast) toast.error(t('errorMessages.api.notFound'));
      break;
    case 500:
      if (!suppressToast) toast.error(t('errorMessages.api.serverError'));
      break;
    case 502:
    case 503:
    case 504:
      if (!suppressToast) toast.error(t('errorMessages.api.serverError'));
      break;
    default:
      // 其他錯誤的統一處理
      if (error.message) {
        // toast.error(error.message);
      } else {
        // toast.error(t('errorMessages.api.unknown'));
      }
      break;
  }
}
